import React, { useState, useRef, useEffect, useMemo } from 'react'
import FormInput from './FormInput'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'
import PlateMemberInviteContent from './PlateMemberInviteContent'
import PlateCoverImage from './PlateCoverImage'
import { isApp } from '../utils/platform'
import { FLUTTER_GALLERY_ERROR_MESSAGE } from '../constants/flutterBridgeMessages'
import { toastUtil } from '../utils/toast'
import { dataURLtoFile } from '../utils/fileUtils'
import { bookmarkApi, InvitableUser, BookmarkCategory } from '../api/bookmark'
import { useAuthStore } from '../stores/authStore'
import Gap from './Gap'
import SheetHeader from './SheetHeader'
import SubmitButton from './SubmitButton'
import ImageCropModal, { ImageCropResult } from './ImageCropModal'
import ImportListContent from './ImportListContent'
import ToggleSwitch from './ToggleSwitch'

/**
 * 플레이트리스트 이름 최대 길이.
 *
 * 표시가 잘리는 건 말줄임(…)이 처리하므로 잘림을 막으려고 조일 필요는 없다.
 * 다만 카드 목록에서는 한글 기준 11~15자만 보이므로(기기 폭에 따라 다름),
 * 그보다 지나치게 긴 이름은 어디서도 읽히지 않는다. 넉넉하되 과하지 않게 20자.
 */
const PLATE_NAME_MAX_LENGTH = 20

interface BookmarkCategoryAddModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (category: {
    id: string
    name: string
    thumbnail_image: string | null
    created_at: string
  }) => void
  categoryId?: string // 기존 카테고리 수정 시 카테고리 ID
  /** true면 바텀시트가 아니라 전체 페이지로 렌더(오버레이·드래그핸들 없음). */
  asPage?: boolean
  /** 수정 진입 시 목록에서 이미 가진 커버 URL을 넘겨받아 API 대기 없이 즉시 표시. */
  initialThumbnail?: string | null
}

const BookmarkCategoryAddModal: React.FC<BookmarkCategoryAddModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  categoryId,
  asPage = false,
  initialThumbnail = null,
}) => {
  const currentUserId = useAuthStore((s) => s.userId)
  const [categoryName, setCategoryName] = useState('')
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(
    initialThumbnail
  )
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // 이미지 크롭 모달 (1:1)
  const [isCropOpen, setIsCropOpen] = useState(false)
  const [cropSource, setCropSource] = useState<string | null>(null)
  const [cropSourceName, setCropSourceName] = useState('cover.jpg')
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const [isMemberInviteOpen, setIsMemberInviteOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [searchUsers, setSearchUsers] = useState<InvitableUser[]>([])
  // 검색 전 추천 목록 = 내가 팔로우하는 사람들 (인스타그램식)
  const [suggestedUsers, setSuggestedUsers] = useState<InvitableUser[]>([])
  const [allUsers, setAllUsers] = useState<InvitableUser[]>([])
  const [inviteDraftIds, setInviteDraftIds] = useState<string[]>([])
  const [inviteDraftUsers, setInviteDraftUsers] = useState<InvitableUser[]>([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [isPrivacyBottomSheetOpen, setIsPrivacyBottomSheetOpen] =
    useState(false)
  const [isPublic, setIsPublic] = useState(true) // 공개 여부 (true: 전체 공개, false: 비공개)
  const [isSubmitting, setIsSubmitting] = useState(false) // 제출 중 상태
  const [isImportOpen, setIsImportOpen] = useState(false) // "장소 가져오기" 하위 시트
  const [importedPlaceIds, setImportedPlaceIds] = useState<number[]>([]) // 링크로 가져온 장소들 (완료 시 연결)
  const invitableSearchDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const inviteDraftIdsRef = useRef<string[]>([])
  inviteDraftIdsRef.current = inviteDraftIds

  // 열린 모달만 FlutterBridge에 콜백을 올려 전역 1개인 onImagesSelected가 닫힌 인스턴스를 가리키지 않게 함
  useEffect(() => {
    if (!isOpen || !isApp() || !(window as any).FlutterBridge) {
      return
    }

    const flutterBridge = (window as any).FlutterBridge

    flutterBridge.onGalleryError = (data: { message?: string }) => {
      toastUtil.error(data?.message ?? FLUTTER_GALLERY_ERROR_MESSAGE)
    }

    flutterBridge.onImagesSelected = (data: any) => {
      if (
        data &&
        data.images &&
        Array.isArray(data.images) &&
        data.images.length > 0
      ) {
        const img = data.images[0]
        const file = dataURLtoFile(img.data, img.name || 'cover-image.jpg')

        if (file.size > 5 * 1024 * 1024) {
          toastUtil.error('파일 크기는 5MB 이하여야 합니다')
          return
        }

        // 크롭 모달 (1:1) 열기 — 결과는 onConfirm에서 적용
        setCropSource(img.data)
        setCropSourceName(img.name || 'cover-image.jpg')
        setIsCropOpen(true)
      }
    }

    return () => {
      if (flutterBridge.onGalleryError) {
        delete flutterBridge.onGalleryError
      }
      if (flutterBridge.onImagesSelected) {
        delete flutterBridge.onImagesSelected
      }
    }
  }, [isOpen])

  const isEditMode = Boolean(categoryId)

  const invitePinnedMemberUsers = useMemo(() => {
    // inviteDraftIds(현재 편집 중인 멤버 목록)에 있는 사용자만 표시. 본인은 제외.
    // (옛 동작: u.is_member도 포함했으나, 그러면 서버 멤버를 X 버튼으로 제거해도
    //          inviteDraftUsers에 남아있어 화면에서 안 사라지는 버그가 있었음.
    //          본인은 멤버 리스트에 표시하지 않음 — 본인 제거는 플레이리스트 삭제 기능으로.)
    const list = inviteDraftUsers.filter(
      (u) => inviteDraftIds.includes(u.id) && u.id !== currentUserId
    )

    return [...list].sort((a, b) =>
      a.nickname.localeCompare(b.nickname, 'ko')
    )
  }, [inviteDraftUsers, inviteDraftIds, currentUserId])

  // 모달이 열릴 때마다 로컬 변수 초기화 또는 기존 데이터 로드
  useEffect(() => {
    if (!isOpen) return

    if (categoryId) {
      // 수정 모드: 기존 데이터 로드
      const loadCategoryData = async () => {
        try {
          const [bookmarkResponse, invitableResponse] = await Promise.all([
            bookmarkApi.getBookmarksByCategory(categoryId),
            bookmarkApi.getCategoryMembers(categoryId),
          ])
          if (bookmarkResponse.categories.length > 0) {
            const category = bookmarkResponse.categories[0] as BookmarkCategory
            setCategoryName(category.name)
            setCoverImagePreview(category.thumbnail_image || null)
            setCoverImageFile(null)
            setIsPublic(category.is_public ?? true)
          }
          const memberIds = invitableResponse.users
            .filter((u) => u.is_member)
            .map((u) => u.id)
          setSelectedMembers(memberIds)
          setAllUsers(invitableResponse.users)
          setSearchUsers([])
        } catch {
          toastUtil.error(
            '플레이트리스트를 불러오는데 실패했습니다'
          )
          onCloseRef.current()
        } finally {
          setIsLoadingUsers(false)
        }
      }
      setIsLoadingUsers(true)
      loadCategoryData()
    } else {
      // 생성 모드: 초기화
      setCategoryName('')
      setCoverImagePreview(null)
      setCoverImageFile(null)
      setSearchValue('')
      setSearchUsers([])
      setAllUsers([])
      setSelectedMembers([])
      setIsPublic(true)
    }
    setIsMemberInviteOpen(false)
    setIsPrivacyBottomSheetOpen(false)
    setIsSubmitting(false)
  }, [isOpen, categoryId])

  const handleCoverImageClick = () => {
    // 앱 환경이면 Flutter 브리지 사용 (단일 이미지 선택)
    if (isApp() && (window as any).FlutterBridge) {
      ;(window as any).FlutterBridge.openGallerySingle()
    } else {
      // 웹 환경이면 기본 file input 사용
      fileInputRef.current?.click()
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toastUtil.error('파일 크기는 5MB 이하여야 합니다')
        return
      }

      if (!file.type.startsWith('image/')) {
        toastUtil.error('이미지 파일만 업로드 가능합니다')
        return
      }

      const reader = new FileReader()
      reader.onloadend = () => {
        // 크롭 모달 (1:1) 열기 — 결과는 onConfirm에서 적용
        setCropSource(reader.result as string)
        setCropSourceName(file.name || 'cover-image.jpg')
        setIsCropOpen(true)
      }
      reader.readAsDataURL(file)
    }
    // 같은 파일 다시 선택 가능하도록 input value 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // 크롭 완료 시 적용
  const handleCropConfirm = (result: ImageCropResult) => {
    setCoverImageFile(result.file)
    setCoverImagePreview(result.dataUrl)
    setIsCropOpen(false)
    setCropSource(null)
  }

  // 크롭 취소
  const handleCropCancel = () => {
    setIsCropOpen(false)
    setCropSource(null)
  }

  const handleSubmit = async () => {
    if (!categoryName.trim()) {
      toastUtil.error('플레이트리스트 이름을 입력해주세요')
      return
    }

    if (isSubmitting) {
      toastUtil.error('이미 제출 중입니다')
      return
    }

    try {
      setIsSubmitting(true)
      // 본인은 항상 멤버에 포함. (UI에서 본인을 안 보여주므로 selectedMembers에는
      // 없을 수 있지만, 백엔드에는 본인 포함 전체 멤버 목록을 보내야 함.)
      const membersToSubmit = currentUserId
        ? Array.from(new Set([...selectedMembers, currentUserId]))
        : selectedMembers

      if (categoryId) {
        // 수정 모드
        await bookmarkApi.updateBookmarkCategoryName(
          categoryId,
          categoryName.trim()
        )
        if (coverImageFile) {
          await bookmarkApi.updateBookmarkCategoryCoverImage(
            categoryId,
            coverImageFile
          )
        }
        await bookmarkApi.updateBookmarkCategoryMembers(
          categoryId,
          membersToSubmit
        )
        await bookmarkApi.updateBookmarkCategoryPrivacy(categoryId, isPublic)

        // 링크로 가져온 장소들을 기존 리스트에 추가 (이미 있는 장소는 제외)
        let addResult: { added: number; duplicates: number } | null = null
        if (importedPlaceIds.length > 0) {
          addResult = await bookmarkApi.addPlacesToCategory(
            categoryId,
            importedPlaceIds
          )
        }

        if (addResult && addResult.added > 0) {
          const dupMsg =
            addResult.duplicates > 0 ? ' (중복 제외)' : ''
          toastUtil.success(`${addResult.added}곳이 추가됐어요${dupMsg}`)
        } else if (addResult && addResult.added === 0) {
          toastUtil.success('이미 리스트에 있는 장소예요')
        } else {
          toastUtil.success('플레이트리스트가 수정되었습니다')
        }

        setImportedPlaceIds([])
        onClose()
        onSuccess({
          id: categoryId,
          name: categoryName.trim(),
          thumbnail_image: coverImagePreview || null,
          created_at: '',
        })
      } else {
        // 생성 모드
        const response = await bookmarkApi.createBookmarkCategory({
          name: categoryName.trim(),
          thumbnailImage: coverImageFile || undefined,
          memberIds: membersToSubmit.length > 0 ? membersToSubmit : undefined,
          isPublic: isPublic,
          placeIds: importedPlaceIds.length > 0 ? importedPlaceIds : undefined,
        })
        toastUtil.success('카테고리가 추가되었습니다')
        setCategoryName('')
        setCoverImagePreview(null)
        setCoverImageFile(null)
        setSelectedMembers([])
        setImportedPlaceIds([])
        onClose()
        onSuccess(response.category)
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        (categoryId
          ? '플레이트리스트 수정에 실패했습니다'
          : '카테고리 추가에 실패했습니다')
      toastUtil.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (invitableSearchDebounceRef.current) {
      clearTimeout(invitableSearchDebounceRef.current)
      invitableSearchDebounceRef.current = null
    }
    setCategoryName('')
    setCoverImagePreview(null)
    setCoverImageFile(null)
    setSearchValue('')
    setSearchUsers([])
    setAllUsers([])
    setSelectedMembers([])
    setInviteDraftIds([])
    setInviteDraftUsers([])
    setIsMemberInviteOpen(false)
    setIsPrivacyBottomSheetOpen(false)
    setIsImportOpen(false)
    setImportedPlaceIds([])
    onClose()
  }

  // "장소 가져오기" 행 탭 → 하위 시트 열기
  const handleOpenImport = () => {
    setIsImportOpen(true)
  }

  // 링크 해석 성공: 가져온 장소들을 보유(완료 시 연결).
  // 추가 모드에선 폴더명을 이름칸에 채우고, 수정 모드에선 기존 이름을 유지(덮어쓰지 않음).
  const handleImportResolved = (
    folderName: string | null,
    placeIds: number[]
  ) => {
    // 링크의 폴더 이름은 '비어 있을 때만' 채운다.
    // 사용자가 이미 이름을 적어 뒀는데 가져오기를 하면 그 이름이 지워지고
    // 네이버·카카오 쪽 이름으로 바뀌던 문제가 있었다.
    if (!isEditMode && !categoryName.trim() && folderName && folderName.trim()) {
      setCategoryName(folderName.trim().slice(0, PLATE_NAME_MAX_LENGTH))
    }
    setImportedPlaceIds(placeIds)
    setIsImportOpen(false)
  }

  // 가게 이름 검색으로 고른 한 곳: 지금까지 담은 목록에 더한다(같은 장소는 한 번만).
  // 가져오기 화면은 닫지 않는다 — 이어서 더 검색할 수 있게.
  const handleImportPlacePicked = (placeId: number) => {
    if (importedPlaceIds.includes(placeId)) return false
    setImportedPlaceIds((prev) =>
      prev.includes(placeId) ? prev : [...prev, placeId]
    )
    return true
  }

  // 설정 메뉴 레이아웃 렌더링 함수 (우측에 현재 값을 함께 표시 가능)
  const renderSettingMenuItem = (
    label: string,
    onClick: () => void,
    rightValue?: string
  ) => {
    return (
      <div
        className="w-full px-20px flex items-center justify-between cursor-pointer"
        onClick={onClick}
      >
        <span className="flex-1 font-pretendard font-bold text-16px leading-24px text-brown-80%-main">
          {label}
        </span>
        {rightValue !== undefined && (
          <span className="font-pretendard font-regular text-15px leading-24px text-textColorText2 whitespace-nowrap">
            {rightValue}
          </span>
        )}
        <Gap width={8} />
        <img
          src="/images/bold-arrow-right-icon.png"
          alt="이동"
          className="w-24px h-24px object-contain"
        />
      </div>
    )
  }

  const loadInviteDraft = async (
    snapshotIds: string[],
    parentUsersSnapshot: InvitableUser[]
  ) => {
    const showSpinner = Boolean(categoryId)
    try {
      if (showSpinner) {
        setIsLoadingUsers(true)
      }

      if (!categoryId) {
        setInviteDraftUsers(
          parentUsersSnapshot
            .filter((u) => snapshotIds.includes(u.id))
            .sort((a, b) =>
              a.nickname.localeCompare(b.nickname, 'ko')
            )
        )
        return
      }

      const response = await bookmarkApi.getCategoryMembers(categoryId)
      const byId = new Map(response.users.map((u) => [u.id, u]))
      for (const id of snapshotIds) {
        if (!byId.has(id)) {
          const prevUser = parentUsersSnapshot.find((u) => u.id === id)
          if (prevUser) {
            byId.set(id, prevUser)
          }
        }
      }

      setInviteDraftUsers(
        Array.from(byId.values()).sort((a, b) =>
          a.nickname.localeCompare(b.nickname, 'ko')
        )
      )
    } catch (error) {
      console.error('사용자 리스트 조회 실패:', error)
      toastUtil.error('사용자 리스트를 불러오는데 실패했습니다')
      setInviteDraftUsers([])
    } finally {
      if (showSpinner) {
        setIsLoadingUsers(false)
      }
    }
  }

  // 검색 전 추천(팔로우) 목록 로드
  const loadSuggestions = async () => {
    try {
      const response = await bookmarkApi.getInvitableUsers(categoryId)
      setSuggestedUsers(response.users.filter((u) => u.id !== currentUserId))
    } catch (error) {
      console.error('추천 사용자 조회 실패:', error)
      setSuggestedUsers([])
    }
  }

  const handleInviteMembers = () => {
    const snapshotIds = [...selectedMembers]
    setInviteDraftIds(snapshotIds)
    setIsMemberInviteOpen(true)
    void loadInviteDraft(snapshotIds, allUsers)
    void loadSuggestions()
  }

  const handleMemberInviteBack = () => {
    if (invitableSearchDebounceRef.current) {
      clearTimeout(invitableSearchDebounceRef.current)
      invitableSearchDebounceRef.current = null
    }
    setSearchValue('')
    setSearchUsers([])
    setIsLoadingUsers(false)
    setIsMemberInviteOpen(false)
  }

  const handleSearchChange = (value: string) => {
    setSearchValue(value)

    if (invitableSearchDebounceRef.current) {
      clearTimeout(invitableSearchDebounceRef.current)
      invitableSearchDebounceRef.current = null
    }

    const trimmed = value.trim()
    if (!trimmed) {
      setSearchUsers([])
      setIsLoadingUsers(false)
      return
    }

    setIsLoadingUsers(true)

    invitableSearchDebounceRef.current = setTimeout(async () => {
      invitableSearchDebounceRef.current = null
      try {
        const response = await bookmarkApi.getInvitableUsers(
          categoryId,
          trimmed
        )
        // 검색 결과에서도 본인은 제외
        setSearchUsers(
          response.users.filter((u) => u.id !== currentUserId)
        )
      } catch (error) {
        console.error('사용자 리스트 조회 실패:', error)
        toastUtil.error('사용자 리스트를 불러오는데 실패했습니다')
        setSearchUsers([])
      } finally {
        setIsLoadingUsers(false)
      }
    }, 300)
  }

  const handleInviteMemberToggle = (userId: string, user?: InvitableUser) => {
    const wasSelected = inviteDraftIdsRef.current.includes(userId)
    if (wasSelected) {
      setInviteDraftIds((prev) => prev.filter((id) => id !== userId))
      setInviteDraftUsers((prev) =>
        prev.filter((u) => u.id !== userId || u.is_member === true)
      )
    } else {
      setInviteDraftIds((prev) => [...prev, userId])
      if (user) {
        setInviteDraftUsers((prev) => {
          if (prev.some((u) => u.id === userId)) {
            return prev
          }

          return [...prev, { ...user, is_member: false }]
        })
      }
    }
  }

  const handleMemberInviteComplete = () => {
    if (invitableSearchDebounceRef.current) {
      clearTimeout(invitableSearchDebounceRef.current)
      invitableSearchDebounceRef.current = null
    }
    setSelectedMembers([...inviteDraftIds])
    setAllUsers([...inviteDraftUsers])
    setSearchValue('')
    setSearchUsers([])
    setIsLoadingUsers(false)
    setIsMemberInviteOpen(false)
  }

  const handlePrivacySettings = () => {
    setIsPrivacyBottomSheetOpen(true)
  }

  const renderMemberInviteContent = () => {
    return (
      <PlateMemberInviteContent
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        pinnedUsers={invitePinnedMemberUsers}
        searchUsers={searchUsers}
        suggestedUsers={suggestedUsers}
        isLoadingUsers={isLoadingUsers}
        selectedMembers={inviteDraftIds}
        onMemberToggle={handleInviteMemberToggle}
        onBack={handleMemberInviteBack}
        onSubmit={handleMemberInviteComplete}
        fill={asPage}
      />
    )
  }

  // 공개 범위 — 멤버 초대처럼 부모 시트 안에서 step 전환(뒤로가기로 복귀)
  const renderPrivacyContent = () => {
    return (
      <>
        <SheetHeader
          title="공개 범위"
          leftAction={
            <button
              type="button"
              onClick={() => setIsPrivacyBottomSheetOpen(false)}
              className="flex items-center justify-center cursor-pointer"
            >
              <img
                src="/images/back-arrow-icon.png"
                alt="뒤로가기"
                draggable={false}
                className="w-48px h-48px object-contain"
              />
            </button>
          }
        />

        <Gap height={28} />

        {/* 비공개 토글 (ON = 비공개) */}
        <div className="w-full px-20px flex items-center justify-between">
          <span className="font-pretendard font-bold text-16px leading-24px text-brown-80%-main">
            비공개 플레이트리스트
          </span>
          <ToggleSwitch
            checked={!isPublic}
            onChange={(checked) => setIsPublic(!checked)}
          />
        </div>

        <Gap height={33} />
      </>
    )
  }

  // 장소 가져오기 — 부모 시트 안에서 step 전환(레이어로 쌓지 않음)
  const renderImportContent = () => {
    return (
      <ImportListContent
        onBack={() => setIsImportOpen(false)}
        onResolved={handleImportResolved}
        onPlacePicked={handleImportPlacePicked}
      />
    )
  }

  // 헤더 (공용) — 페이지 모드에선 좌측에 뒤로가기
  const renderDefaultHeader = () => (
    <SheetHeader
      title={isEditMode ? '플레이트리스트 수정' : '플레이트리스트 추가'}
      leftAction={
        asPage ? (
          <button
            type="button"
            onClick={handleClose}
            className="flex items-center justify-center cursor-pointer"
          >
            <img
              src="/images/back-arrow-icon.png"
              alt="뒤로가기"
              draggable={false}
              className="w-48px h-48px object-contain"
            />
          </button>
        ) : undefined
      }
    />
  )

  // 커버 이미지 + '커버 변경' 라벨 (페이지 모드는 + 오버레이 없이)
  const renderCoverBlock = () => (
    <div
      className="w-full flex flex-col items-center cursor-pointer"
      onClick={handleCoverImageClick}
    >
      <PlateCoverImage
        imageUrl={coverImagePreview}
        isAddPlusImg={!asPage}
        width={243}
        height={176}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <Gap height={16} />

      <span className="font-pretendard font-bold text-16px leading-125% text-brown-80%-main text-center">
        플레이트리스트 커버 변경
      </span>
    </div>
  )

  const renderNameInput = () => (
    <>
      <FormInput
        label=""
        name="categoryName"
        type="text"
        value={categoryName}
        onChange={(e) =>
          setCategoryName(e.target.value.slice(0, PLATE_NAME_MAX_LENGTH))
        }
        placeholder="플레이트리스트 이름을 입력하세요"
        maxLength={PLATE_NAME_MAX_LENGTH}
        marginBottom={0}
        addClassName="px-20px"
      />
      {/* 제한이 있다는 걸 미리 알려주면 저장할 때 놀라지 않는다 */}
      <div className="px-20px pt-6px flex justify-end">
        <span className="font-pretendard font-regular text-12px leading-125% text-textColorText3">
          {categoryName.length}/{PLATE_NAME_MAX_LENGTH}
        </span>
      </div>
    </>
  )

  const renderSettingRows = () => (
    <>
      {/* 멤버 초대 (우측에 초대 인원수 표시, 본인 제외) */}
      {renderSettingMenuItem(
        '멤버 초대',
        handleInviteMembers,
        String(selectedMembers.filter((id) => id !== currentUserId).length)
      )}

      <Gap height={28} />

      {/* 공개 범위 (우측에 현재 값 표시) */}
      {renderSettingMenuItem(
        '공개 범위',
        handlePrivacySettings,
        isPublic ? '전체 공개' : '비공개'
      )}

      {/* 장소 가져오기 (네이버/카카오 링크) — 추가/수정 모드 모두 표시 */}
      <Gap height={28} />
      {renderSettingMenuItem('장소 가져오기', handleOpenImport)}
    </>
  )

  // 시트 모드용 본문(한 흐름)
  const renderDefaultBody = () => (
    <>
      <Gap height={36} />
      {renderCoverBlock()}
      <Gap height={36} />
      {renderNameInput()}
      <Gap height={24} />
      {renderSettingRows()}
    </>
  )

  const renderCompleteButton = () => (
    <SubmitButton
      text="완료"
      onSubmit={handleSubmit}
      onDisabled={handleSubmit}
      disabled={!categoryName.trim() || isSubmitting}
      loading={isSubmitting}
    />
  )

  // 시트 모드용: 헤더+본문+완료를 한 흐름으로
  const renderDefaultContent = () => (
    <>
      {renderDefaultHeader()}
      {renderDefaultBody()}
      <Gap height={32} />
      <div className="px-20px">{renderCompleteButton()}</div>
      <Gap height={32} />
    </>
  )

  const { handleProps, sheetStyle } = useSheetDragDismiss({
    onClose: handleClose,
    // 페이지 모드에선 드래그 닫기/배경 스크롤 잠금/스와이프 억제를 끈다(일반 페이지처럼 동작)
    enabled: isOpen && !asPage,
  })

  // 스텝 콘텐츠(모달·페이지 공통). 모달 모드에선 드래그핸들 아래 여백을 준다.
  const stepContent = isMemberInviteOpen ? (
    <>
      {!asPage && <Gap height={8} />}
      {renderMemberInviteContent()}
    </>
  ) : isPrivacyBottomSheetOpen ? (
    <>
      {!asPage && <Gap height={8} />}
      {renderPrivacyContent()}
    </>
  ) : isImportOpen ? (
    <>
      {!asPage && <Gap height={8} />}
      {renderImportContent()}
    </>
  ) : (
    <>
      {!asPage && <Gap height={20} />}
      {renderDefaultContent()}
    </>
  )

  // 페이지 모드: 오버레이·드래그핸들 없는 전체 페이지.
  // 기본 화면은 헤더 고정(상단) + 본문 스크롤 + 완료 버튼 하단 고정.
  // 하위 스텝(멤버초대·공개범위·장소가져오기)은 자체 헤더가 있어 전체를 스크롤 영역에.
  if (asPage) {
    const isSubStep =
      isMemberInviteOpen || isPrivacyBottomSheetOpen || isImportOpen
    return (
      <>
        <div className="w-full h-full flex flex-col bg-ivoryKey">
          <div className="app-container w-full h-full flex flex-col">
            {isSubStep ? (
              // 하위 스텝은 각자 자기 레이아웃(스크롤·하단 고정 버튼)을 관리한다
              <div className="flex-1 flex flex-col min-h-0">{stepContent}</div>
            ) : (
              <>
                {renderDefaultHeader()}
                {/* 본문: 커버 위 여백 ≈ 라벨→이름 여백 이 되도록 동일 flex 스페이서 2개로
                    분배(시안 대응·반응형). 이름+설정행은 하단으로 밀리고 완료는 고정. */}
                <div className="flex-1 flex flex-col overflow-y-auto scrollbar-hide">
                  <div className="flex-1 min-h-24px" />
                  {renderCoverBlock()}
                  <div className="flex-1 min-h-24px" />
                  {renderNameInput()}
                  <Gap height={28} />
                  {renderSettingRows()}
                  {/* 장소 가져오기 ↔ 완료 버튼 사이 간격 42px */}
                  <Gap height={42} />
                </div>
                {/* 하단 버튼: 앱 표준 pb-32px (바닥 32px 고정) */}
                <div className="px-20px pb-32px">
                  {renderCompleteButton()}
                </div>
              </>
            )}
          </div>
        </div>

        <ImageCropModal
          isOpen={isCropOpen}
          imageDataUrl={cropSource}
          aspect={1}
          fileName={cropSourceName}
          title="플레이트리스트 커버 편집"
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      </>
    )
  }

  if (!isOpen) return null

  return (
    <>
      {/* 오버레이 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1040]"
        onClick={handleClose}
      />

      {/* 바텀 시트 컨테이너 */}
      <div className="fixed bottom-0 left-0 right-0 z-[1050] flex justify-center">
        <div className="app-container flex flex-col" style={sheetStyle}>
          {/* 바텀 시트 */}
          <div
            className={`
              bg-ivoryKey
              rounded-t-30px
              shadow-lg
              overflow-hidden
              flex
              flex-col
              w-full
            `}
          >
            {/* 드래그 핸들 (아래로 끌면 닫힘) */}
            <div className="flex flex-col w-full" {...handleProps}>
              <Gap height={24} />
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto"></div>
            </div>

            {stepContent}
          </div>
        </div>
      </div>

      {/* 커버 이미지 크롭 모달 (1:1) */}
      <ImageCropModal
        isOpen={isCropOpen}
        imageDataUrl={cropSource}
        aspect={1}
        fileName={cropSourceName}
        title="플레이트리스트 커버 편집"
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
      />
    </>
  )
}

export default BookmarkCategoryAddModal
